import React, { FC } from 'react';
import { AuthFlowAction } from '@/components/blocks/AuthentificationFlow';
import { Button, Divider, Drawer, IconButton, Paper, Stack, Typography } from '@mui/material';
import {
	DrawerRegisterContentSx,
	DrawerRegisterFrameSx,
} from '@/components/content/Cart/parts/DrawerRegister/styles/main';
import { Register } from '@/components/content/Register';
import {
	DrawerLoginFrameSx,
	DrawerLoginTitleSx,
} from '@/components/content/Cart/parts/DrawerLogin/styles/main';
import { SingleLoginForm } from '@/components/blocks/SingleLoginForm';
import { useRecaptchaV3Setup } from '@/data/Content/Recaptcha';
import { useSWRConfig } from 'swr';
import { personMutatorKeyMatcher, useLogin, UserLogon } from '@/data/Content/Login';
import { useLocalization } from '@/data/Localization';
import {
	createAccountButtonSX,
	createAccountLabelSX,
} from '@/components/content/Login/styles/button';
import CloseIcon from '@mui/icons-material/Close';
import {useSuccessLoginNotify} from '@/utils/useSuccessLoginNotify';
import { useReplaceAuthRoute } from 'integration/hooks/useReplaceAuthRoute';

type AuthFlowDrawerProps = {
	action: AuthFlowAction;
	open: boolean;
	onClose: () => void;
	onGo: (action: AuthFlowAction) => void;
};

export const AuthFlowDrawer: FC<AuthFlowDrawerProps> = ({ action, open, onClose, onGo }) => {
	const { recaptchaEnabled, recaptchaExecute, recaptchaLoading } = useRecaptchaV3Setup();
	const { mutate } = useSWRConfig();
	const { loginSubmit } = useLogin();
	const signInNLS = useLocalization('SignIn');
	const noAccountMsg = signInNLS.noAccount.t();
	const sigIn = signInNLS.registerNow.t();
	const {showSuccessAuthUser} = useSuccessLoginNotify();
	const { onReplaceAuthRoute } = useReplaceAuthRoute({ shouldCallToModal: true });

	const handleSubmit = async (props: UserLogon) => {
		let x_recaptchaToken: string | undefined = undefined;

		if (recaptchaEnabled) {
			x_recaptchaToken = await recaptchaExecute('login');
		}
		const response = await loginSubmit({ ...props, x_recaptchaToken });
		if ('userId' in response ?? {}) {
			await mutate(personMutatorKeyMatcher(''), undefined);
			await showSuccessAuthUser();
			await onReplaceAuthRoute();
			onClose();
		}
	};


	return (
		<Drawer open={open} onClose={onClose}>
			{action === AuthFlowAction.LOGIN ? (
				<Paper sx={DrawerLoginFrameSx}>
					<IconButton
						onClick={onClose}
						sx={{
							ml: 'auto',
						}}
					>
						<CloseIcon />
					</IconButton>
					<Stack
						sx={{
							width: '100%',
							padding: '0px 30px 40px 30px',
						}}
					>
						<Typography sx={DrawerLoginTitleSx}>{'INICIA SESIÓN'}</Typography>
						<SingleLoginForm
							forgotPasswordPosition="bottom"
							onSubmit={handleSubmit}
							disabled={recaptchaLoading}
						/>
						<Divider sx={{ marginY: '15px' }} />
						<Stack alignItems="center" spacing={'13px'}>
							<Typography sx={createAccountLabelSX}>{noAccountMsg}</Typography>
							<Button
								type="button"
								variant="contained"
								sx={createAccountButtonSX}
								onClick={() => onGo(AuthFlowAction.REGISTER)}
							>
								{sigIn}
							</Button>
						</Stack>
					</Stack>
				</Paper>
			) : (
				<Paper sx={DrawerRegisterFrameSx}>
					<Stack sx={DrawerRegisterContentSx}>
						<Register
							id={''}
							withoutLogin
							adaptWith
							shouldCallToModal
							onGoBack={() => onGo(AuthFlowAction.LOGIN)}
							onClose={onClose}
						/>
					</Stack>
				</Paper>
			)}
		</Drawer>
	);
};
